/**
 * Iran Attack Tracking layer.
 * Filters Iran-related events from the event store and renders:
 * - Directional arcs from Iran to event locations (attacks, incidents)
 * - Point markers at event locations
 * Cross-references with OREF (Israel Home Front Command) keywords.
 */

import { ArcLayer, ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent, Severity } from '@/sources/base-source';

/** Layer IDs handled by this renderer. */
export const IRAN_ATTACK_LAYER_IDS = new Set(['iran-attacks']);

// ---------------------------------------------------------------------------
// Iran detection
// ---------------------------------------------------------------------------

/** Iran coordinates (Tehran) — arc source. */
const IRAN_ORIGIN: [number, number] = [51.389, 35.689]; // [lng, lat]

/** Keywords that indicate an Iran-related event. Case-insensitive matching. */
const IRAN_KEYWORDS = [
  'iran', 'iranian', 'irgc', 'quds force', 'tehran',
  'hezbollah', 'houthi', 'houthis',
  'islamic republic', 'khamenei', 'raisi',
  'strait of hormuz', 'hormuz',
];

/** OREF / Israel Home Front Command keywords for cross-referencing. */
const OREF_KEYWORDS = [
  'oref', 'home front command', 'red alert', 'tzeva adom',
  'iron dome', 'rocket attack', 'missile attack', 'drone attack',
  'barrage', 'interception', 'projectile',
];

/** Combined keyword regex — built once. */
const IRAN_REGEX = new RegExp(
  `\\b(${IRAN_KEYWORDS.join('|')})\\b`,
  'i',
);

const OREF_REGEX = new RegExp(
  `\\b(${OREF_KEYWORDS.join('|')})\\b`,
  'i',
);

/** Check if an event is Iran-related. */
function isIranRelated(event: NormalizedEvent): boolean {
  // Check title
  if (IRAN_REGEX.test(event.title)) return true;

  // Check description
  if (event.description && IRAN_REGEX.test(event.description)) return true;

  // Check tags
  if (event.tags?.some(t => IRAN_REGEX.test(t))) return true;

  // Check country
  if (event.location?.country) {
    const c = event.location.country.toLowerCase();
    if (c === 'iran' || c === 'ir') return true;
  }
  if (event.location?.countryCode === 'IR') return true;

  return false;
}

/** Check if an event is OREF-related (Israel rocket alerts). */
function isOrefRelated(event: NormalizedEvent): boolean {
  if (OREF_REGEX.test(event.title)) return true;
  if (event.description && OREF_REGEX.test(event.description)) return true;
  if (event.tags?.some(t => OREF_REGEX.test(t))) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

type RGBA = [number, number, number, number];

const SEVERITY_ARC_COLORS: Record<Severity, { source: RGBA; target: RGBA }> = {
  critical: { source: [239, 68, 68, 200], target: [239, 68, 68, 120] },
  high:     { source: [245, 158, 11, 180], target: [245, 158, 11, 100] },
  medium:   { source: [234, 179, 8, 160], target: [234, 179, 8, 80] },
  low:      { source: [34, 197, 94, 140], target: [34, 197, 94, 60] },
  info:     { source: [100, 116, 139, 120], target: [100, 116, 139, 60] },
};

const OREF_COLOR: RGBA = [255, 60, 60, 220];

// ---------------------------------------------------------------------------
// WM point tiers (services/conflict/index.ts:215-251)
// ---------------------------------------------------------------------------

/**
 * WM's category sets. WM's feed carries an explicit category; Sentinel
 * derives it from the event text — military/kinetic terms map to the red
 * set, political/diplomatic terms to the orange set. OREF matches are
 * kinetic by definition.
 */
// Bare 'strike' (labor strikes) and 'defense' (defense-minister statements)
// are deliberately absent — measured false-positive sources.
const IRAN_MILITARY_REGEX = /\b(airstrike|air strike|missile|rocket|drone|attack|military|interception|iron dome|barrage|explosion|shelling)\b/i;
const IRAN_POLITICAL_REGEX = /\b(diplomacy|diplomatic|sanction|sanctions|talks|negotiation|political|election|minister|parliament|united nations|ceasefire|statement)\b/i;

type IranTier = 'red' | 'orange' | 'yellow';

/**
 * Tier resolution matching WM (services/conflict/index.ts:220-224):
 * critical severity or a military category -> red; political -> orange;
 * everything else -> yellow.
 */
export function getIranTier(event: NormalizedEvent, isOref: boolean): IranTier {
  if (event.severity === 'critical' || isOref) return 'red';
  const text = `${event.title} ${event.description ?? ''}`;
  if (IRAN_MILITARY_REGEX.test(text)) return 'red';
  if (IRAN_POLITICAL_REGEX.test(text)) return 'orange';
  return 'yellow';
}

/** WM IRAN_RGBA (services/conflict/index.ts:226-228). */
const IRAN_TIER_COLORS: Record<IranTier, RGBA> = {
  red: [255, 50, 50, 220],
  orange: [255, 165, 0, 200],
  yellow: [255, 255, 0, 180],
};

/** WM severity radii in meters (services/conflict/index.ts:247-251). */
export function getIranRadius(severity: Severity | undefined): number {
  if (severity === 'critical' || severity === 'high') return 20_000;
  if (severity === 'medium') return 15_000;
  return 10_000;
}

// ---------------------------------------------------------------------------
// Layer factory
// ---------------------------------------------------------------------------

interface IranArcDatum {
  event: NormalizedEvent;
  source: [number, number]; // Iran origin [lng, lat]
  target: [number, number]; // Event location [lng, lat]
  isOref: boolean;
}

/** Create deck.gl layers for Iran attack tracking. */
export function createIranAttackLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  // Filter passed-in events (already time-filtered by orchestrator) for Iran relevance
  const iranEvents = events.filter(e =>
    e.location && (isIranRelated(e) || isOrefRelated(e)),
  );

  if (iranEvents.length === 0) return [];

  const layers: Layer[] = [];
  const opacity = Math.min(1, 0.3 + zoom * 0.1);

  // Build arc data — arcs from Iran to each event location
  // Skip events that are in Iran itself (no arc to self)
  const arcData: IranArcDatum[] = [];
  const pointEvents: NormalizedEvent[] = [];

  for (const event of iranEvents) {
    if (!event.location) continue;

    const isInIran = event.location.countryCode === 'IR' ||
      (event.location.country?.toLowerCase() === 'iran');

    const isOref = isOrefRelated(event);

    // Only draw arcs for events outside Iran
    if (!isInIran) {
      arcData.push({
        event,
        source: IRAN_ORIGIN,
        target: [event.location.lng, event.location.lat],
        isOref,
      });
    }

    pointEvents.push(event);
  }

  // Directional arcs from Iran to target locations
  if (arcData.length > 0) {
    layers.push(new ArcLayer<IranArcDatum>({
      id: 'iran-attacks-arcs',
      data: arcData,
      pickable: true,
      getSourcePosition: (d) => d.source,
      getTargetPosition: (d) => d.target,
      getSourceColor: (d) => {
        if (d.isOref) return OREF_COLOR;
        const sev = d.event.severity ?? 'info';
        return SEVERITY_ARC_COLORS[sev].source;
      },
      getTargetColor: (d) => {
        if (d.isOref) return [255, 60, 60, 100] as RGBA;
        const sev = d.event.severity ?? 'info';
        return SEVERITY_ARC_COLORS[sev].target;
      },
      getWidth: (d) => d.isOref ? 2.5 : 1.5,
      greatCircle: true,
      opacity,
      updateTriggers: {
        getSourceColor: [arcData.length],
        getTargetColor: [arcData.length],
      },
    }));
  }

  // Point markers — WM's 3-tier color (category+severity) and severity-
  // stepped meter radii (DeckGLMap.ts:3373-3382); alpha lives in the fill
  layers.push(new ScatterplotLayer<NormalizedEvent>({
    id: 'iran-attacks-points',
    data: pointEvents,
    pickable: true,
    filled: true,
    stroked: true,
    lineWidthUnits: 'pixels' as const,
    lineWidthMinPixels: 1,
    radiusUnits: 'meters' as const,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: (d) => IRAN_TIER_COLORS[getIranTier(d, isOrefRelated(d))],
    getLineColor: [255, 255, 255, 100],
    getLineWidth: 1,
    getRadius: (d) => getIranRadius(d.severity),
    radiusMinPixels: 4,
    radiusMaxPixels: 16,
    updateTriggers: {
      getFillColor: [pointEvents.length],
      getRadius: [pointEvents.length],
    },
  }));

  // Invisible wide pick target (WM DeckGLMap.ts:2021) — small dots stay clickable
  layers.push(new ScatterplotLayer<NormalizedEvent>({
    id: 'iran-attacks-pick-ghost',
    data: pointEvents,
    pickable: true,
    filled: true,
    radiusUnits: 'meters' as const,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: [0, 0, 0, 0],
    getRadius: (d) => getIranRadius(d.severity),
    radiusMinPixels: 12,
  }));

  return layers;
}
